import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap, BehaviorSubject, catchError, of } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { logDebug, logWarn, logError } from '../core/services/logger.service';

interface VerifyTokenResponse {
  success: boolean;
  message: string;
  token: string;
}

export interface DecodedToken {
  sub: string;
  name: string;
  username?: string;
  iat: number;
  exp: number;
  apikey: string;
  organization: string;
  role?: 'agent' | 'admin' | 'platform';
}

@Injectable({
  providedIn: 'root'
})
export class JwtService {
  private readonly EXTERNAL_AUTH_URL = environment.externaleAuthUrl;
  private readonly VERIFY_TOKEN_URL = `${environment.apiUrl}/auth/login`;
  private readonly VERSION_NUMBER = environment.versionNumber;
  private readonly LOGOUT_URL = `${environment.apiUrl}/auth/logout`;
  
  // BehaviorSubject pour observer les changements du token
  private tokenSubject = new BehaviorSubject<DecodedToken | null>(null);
  
  // Observable public pour que les composants puissent s'abonner
  public token$ = this.tokenSubject.asObservable();
  
  // Flag pour éviter les vérifications multiples
  private hasCheckedUrl = false;

  constructor(private http: HttpClient) {
    // Initialiser le token au démarrage
    this.initializeToken();
  }

  /**
   * Charge le token depuis la configuration de développement
   * Utilisé uniquement en mode développement
   */
  private loadDevToken(): string | null {
    if (!environment.production) {
      // Récupérer le token depuis le fichier environment
      const devToken = (environment as any).devJwtToken;
      if (devToken && devToken !== 'your-jwt-token-here') {
        logDebug('Dev token loaded from configuration');
        return devToken;
      }
    }
    return null;
  }

  /**
   * Initialise le token au démarrage du service
   * Vérifie d'abord l'URL, puis le sessionStorage, puis le token de dev
   */
  private initializeToken(): void {

    // Vérifier d'abord s'il y a un token dans l'URL
    // SECURITE: le token est immédiatement nettoyé de l'URL après extraction
    // pour éviter qu'il persiste dans l'historique navigateur ou les headers Referer
    const tokenFromUrl = this.getTokenFromUrl();
    if (tokenFromUrl) {
      this.clearTokenFromUrl();
      this.setToken(tokenFromUrl);
      this.hasCheckedUrl = true;
      return;
    }

    // Sinon, vérifier le sessionStorage
    const decodedToken = this.decodeToken();
    if (decodedToken) {
      this.tokenSubject.next(decodedToken);
      return;
    }

    // En mode développement, charger le token depuis la configuration
    const devToken = this.loadDevToken();
    if (devToken) {
      this.setToken(devToken);
      return;
    }

    // Aucun token trouvé
    this.tokenSubject.next(null);
  }

  /**
   * Vérifie si le token JWT est valide en appelant l'API externe
   * @returns Observable<boolean>
   */
  public verifyTokenWithApi(): Observable<boolean> {
    const token = this.getToken();
    
    if (!token) {
      return new Observable<boolean>(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    return this.http.post<VerifyTokenResponse>(this.VERIFY_TOKEN_URL, { token },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    )
      .pipe(
        map(response => response.success === true),
        catchError((error) => {
          logError('Token verification failed');
          return of(false);
        })
      );
  }

  /**
   * Récupère le token depuis le sessionStorage
   * @returns string | null
   */
  public getToken(): string | null {
    const token = sessionStorage.getItem('startupkit_SESSION');
    return token;
  }

  /**
   * Sauvegarde le token dans le sessionStorage
   * @param token string
   */
  public setToken(token: string): void {
    sessionStorage.setItem('startupkit_SESSION', token);
    // Émettre le nouveau token décodé
    const decodedToken = this.decodeToken();
    this.tokenSubject.next(decodedToken);
  }

  /**
   * Supprime le token du sessionStorage
   * @param redirectToLogin - Si true, redirige automatiquement vers la page de login
   */
  public removeToken(redirectToLogin: boolean = false): void {
    this.clearLocalToken();

    this.removeTokenUser().subscribe({
      error: (error) => {
        logError('Logout API call failed:', error);
      }
    });

    // Rediriger si demandé
    if (redirectToLogin) {
      this.redirectLoginPage();
    }
  }

  private clearLocalToken(): void {
    sessionStorage.removeItem('startupkit_SESSION');
    // Émettre null pour indiquer qu'il n'y a plus de token
    this.tokenSubject.next(null);
  }

  private removeTokenUser(): Observable<boolean> {
    const token = this.getToken();

    if (!token) {
      return of(false);
    }

    return this.http.post<{ success?: boolean }>(this.LOGOUT_URL, { token }, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).pipe(
      map(res => res?.success === true),
      catchError(() => of(false))
    );
  }

  /**
   * Vérifie le token avec l'API et redirige si nécessaire
   * @returns Observable<boolean>
   */
  public checkTokenAndRedirect(): Observable<boolean> {
    // Vérifier l'URL seulement si on n'a pas encore vérifié et qu'on n'a pas de token en session
    const existingToken = this.getToken();
    
    if (!existingToken && !this.hasCheckedUrl) {
      const tokenFromUrl = this.getTokenFromUrl();
      if (tokenFromUrl) {
        this.setToken(tokenFromUrl);
        // Nettoyer l'URL après avoir récupéré le token
        this.clearTokenFromUrl();
      }
      this.hasCheckedUrl = true;
    }

    return this.verifyTokenWithApi().pipe(
      map(isValid => {
        if (!isValid) {
          this.removeToken();
          // Réinitialiser le flag si le token est invalide
          this.hasCheckedUrl = false;
          // Ne pas rediriger ici, laisser le guard s'en charger
          return false;
        }
        return true;
      }),
      catchError((error) => {
        logError('Error in checkTokenAndRedirect:', error);
        this.removeToken();
        // Réinitialiser le flag en cas d'erreur
        this.hasCheckedUrl = false;
        return of(false);
      })
    );
  }

  public redirectLoginPage(): void {
    window.location.href = this.EXTERNAL_AUTH_URL;
  }

  /**
   * Récupère le token depuis les paramètres de l'URL
   * @returns string | null
   */
  public getTokenFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  }

  /**
   * Nettoie le token de l'URL après l'avoir récupéré
   */
  private clearTokenFromUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Décode le token JWT
   * @returns DecodedToken | null
   */
  public decodeToken(): DecodedToken | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const decoded = jwtDecode<DecodedToken>(token);
      return decoded;
    } catch (error) {
      logError('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Met à jour l'observable avec le token actuel
   * Utile pour forcer une mise à jour
   */
  public updateTokenObservable(): void {
    const decodedToken = this.decodeToken();
    this.tokenSubject.next(decodedToken);
  }
}
